import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { AuthResponse } from '../auth/auth.models';
import { CommonService } from '../services/common.service';

const REFRESH_PATH = '/api/v1/auth/refresh';
const PUBLIC_AUTH_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/password-reset/request',
  '/api/v1/auth/password-reset/confirm',
  /** Emailed position-export token redeem — public, no JWT. */
  '/api/v1/position-exports/download-url',
] as const;

export const AUTH_REFRESH_ATTEMPTED = new HttpContextToken<boolean>(() => false);

let refreshInFlight$: Observable<AuthResponse> | null = null;

function isPublicAuthRequest(url: string): boolean {
  return PUBLIC_AUTH_PATHS.some(path => url.includes(path));
}

function isRefreshRequest(url: string): boolean {
  return url.includes(REFRESH_PATH);
}

function isApiRequest(url: string): boolean {
  return url.startsWith(environment.apiBaseUrl);
}

function shouldAttemptRefresh(request: HttpRequest<unknown>, err: HttpErrorResponse): boolean {
  return (
    err.status === 403 &&
    isApiRequest(request.url) &&
    !request.context.get(AUTH_REFRESH_ATTEMPTED) &&
    !isPublicAuthRequest(request.url) &&
    !isRefreshRequest(request.url)
  );
}

function getOrStartRefresh(auth: AuthService): Observable<AuthResponse> {
  if (!refreshInFlight$) {
    refreshInFlight$ = auth.refreshTokens().pipe(
      finalize(() => {
        refreshInFlight$ = null;
      }),
      shareReplay(1),
    );
  }
  return refreshInFlight$;
}

function handleRefreshFailure(auth: AuthService, router: Router, common: CommonService): void {
  auth.logout();
  void router.navigate(['/auth/sign-in'], {
    queryParams: {
      returnUrl: router.url.startsWith('/auth') ? '/' : router.url,
      sessionExpired: true,
    },
  });
  common.notifyMessage('Your session has expired. Please sign in again.', 'error');
}

function retryWithFreshToken(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  accessToken: string | null,
): Observable<HttpEvent<unknown>> {
  if (!accessToken) {
    return throwError(() => new HttpErrorResponse({
      status: 401,
      statusText: 'Unauthorized',
      url: request.url,
      error: { message: 'Session refresh did not return an access token.' },
    }));
  }

  return next(
    request.clone({
      context: request.context.set(AUTH_REFRESH_ATTEMPTED, true),
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    }),
  );
}

export function shouldSkipSessionExpiryHandling(request: HttpRequest<unknown>, err: HttpErrorResponse): boolean {
  return shouldAttemptRefresh(request, err) || (isRefreshRequest(request.url) && (err.status === 400 || err.status === 401));
}

export const refreshInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const common = inject(CommonService);

  return next(request).pipe(
    catchError((err: unknown) => {
      const httpErr = err instanceof HttpErrorResponse ? err : null;
      if (!httpErr || !shouldAttemptRefresh(request, httpErr)) {
        return throwError(() => err);
      }

      return getOrStartRefresh(auth).pipe(
        switchMap(() => retryWithFreshToken(request, next, auth.getAccessToken())),
        catchError((refreshErr: unknown) => {
          handleRefreshFailure(auth, router, common);
          return throwError(() => refreshErr);
        }),
      );
    }),
  );
};
